import { iptv, sportApi, type League, type SportEvent } from './api';

/** Sportwidget met competitielogo's: gedeelde data en samenvatting per competitie. */

let leaguesP: Promise<League[]> | null = null;
export function loadLeagues(): Promise<League[]> {
  leaguesP ??= sportApi.leagues().then((r) => r.leagues).catch((e: unknown) => {
    leaguesP = null;
    throw e;
  });
  return leaguesP;
}

let eventsP: { at: number; p: Promise<SportEvent[]> } | null = null;
/** Sportgids voor een week vooruit; 2 minuten bewaard zodat widget en pagina dezelfde data delen. */
export function loadWeek(force = false): Promise<SportEvent[]> {
  if (!force && eventsP && Date.now() - eventsP.at < 120_000) return eventsP.p;
  const p = iptv.sport(168).then((r) => r.events);
  eventsP = { at: Date.now(), p };
  p.catch(() => {
    eventsP = null;
  });
  return p;
}

export interface LeagueSummary {
  league: League;
  live: number;
  today: number;
  week: number;
  next: SportEvent | null;
  /** Sorteervolgorde: live, dan vandaag, dan later, dan niets */
  rank: number;
}

function isToday(ms: number, now: number): boolean {
  return new Date(ms).toDateString() === new Date(now).toDateString();
}

export function summarize(leagues: League[], events: SportEvent[], pick?: string[], now = Date.now()): LeagueSummary[] {
  const wanted = pick?.length ? pick : leagues.filter((l) => l.default).map((l) => l.id);
  const byId = new Map(leagues.map((l) => [l.id, l]));
  const out: LeagueSummary[] = [];
  wanted.forEach((id, order) => {
    const league = byId.get(id);
    if (!league) return;
    const evs = events.filter((e) => e.league === id && e.stop > now);
    const live = evs.filter((e) => e.live).length;
    const upcoming = evs.filter((e) => !e.live).sort((a, b) => a.start - b.start);
    const today = upcoming.filter((e) => isToday(e.start, now)).length;
    const next = upcoming[0] ?? null;
    const tier = live ? 0 : today ? 1 : next ? 2 : 3;
    out.push({ league, live, today, week: evs.length, next, rank: tier * 10_000 + (next ? Math.min(9_999, Math.floor((next.start - now) / 60_000)) : 0) + order / 1000 });
  });
  return out.sort((a, b) => a.rank - b.rank);
}

export function statusText(s: LeagueSummary, now = Date.now()): { text: string; tone: 'live' | 'today' | 'later' | 'none' } {
  const clock = (ms: number) => new Date(ms).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  if (s.live) return { text: `Live · ${s.live === 1 ? '1 uitzending' : `${s.live} uitzendingen`}`, tone: 'live' };
  if (s.next && isToday(s.next.start, now)) return { text: `Vandaag ${clock(s.next.start)}${s.today > 1 ? ` · ${s.today}×` : ''}`, tone: 'today' };
  if (s.next) {
    const d = new Date(s.next.start);
    const tomorrow = new Date(now + 86_400_000).toDateString() === d.toDateString();
    const day = tomorrow ? 'Morgen' : d.toLocaleDateString('nl-NL', { weekday: 'short' }).replace('.', '');
    return { text: `${day.charAt(0).toUpperCase()}${day.slice(1)} ${clock(s.next.start)}`, tone: 'later' };
  }
  return { text: 'Niets deze week', tone: 'none' };
}
